import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import SignatureCanvas from 'react-signature-canvas';

import { White } from '../../constants/Colors';

import ThemeContext from '../../theme/ThemeContext';

import Modal from './Modal';
import Button from './Button';

const StyledWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const StyledSignatureMeasurer = styled.div`
  position: relative;
  margin-bottom: 8px;
  display: flex;
  flex: 1;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  background-color: ${White};
  border-radius: ${(props) => props.theme.borderRadius}px;
  border: 1px solid ${(props) => props.theme.borderColor};
  max-height: 200px;
`;

const StyledSignatureLine = styled.div`
  position: absolute;
  border-bottom: 1px dashed ${(props) => props.theme.borderColor};
  bottom: 54px;
  width: 80%;
  margin-right: auto;
  margin-left: auto;
`;

const StyledSubText = styled.span`
  font-size: 11px;
  color: ${(props) => props.theme.fontColorSecondary};
`;

const StyledActionsWrapper = styled.div`
  margin-bottom: 8px;
  justify-content: space-between;
  align-items: center;
  display: flex;
  flex-direction: row;
`;

const StyledInputWrapper = styled.div`
  align-self: flex-start;
`;

const StyledInput = styled.input`
  display: flex;
  flex: 1;
  background-color: ${(props) => props.theme.backgroundColor};
  font-size: ${(props) => props.theme.fontSize};
  color: ${(props) => props.theme.fontColorPrimary};
  border-radius: ${(props) => props.theme.borderRadius}px;
  border: ${(props) => props.theme.borderWidth}px solid ${(props) => props.theme.borderColor};
  padding: 8px;
  transition: all 0.15s ease-in-out;

  &:hover, &:focus {
    outline: unset; 
    border-style: solid;
    border-width: 1px !important;
    border-right-color: ${(props) => props.theme.primaryColor} !important;
    border-left-color: ${(props) => props.theme.primaryColor} !important;
    border-color: ${(props) => props.theme.primaryColor};
  }

`;

const StyledButtonsWrapper = styled.div`
  align-self: flex-end;
  justify-content: space-between;
`;

class SignatureModal extends PureComponent {

  static contextType = ThemeContext

  static propTypes = {

    id: PropTypes.string,
    display: PropTypes.bool,
    primaryDisplayOnly: PropTypes.bool,

    //Action handlers
    onSubmit: PropTypes.func,
    onHide: PropTypes.func,
    onRef: PropTypes.func, //Used for testing purposes

    width: PropTypes.number,

  }

  constructor(props) {
    super(props);
    this.state = {
      changed: false,
      confirmingClear: false,
      signatureOffsets: null,
      signer: '',
    };
  }

  render() {

    const {
      id,
      disabled,
      onRef,
      display,
      onHide,
      primaryDisplayOnly,
      width,
    } = this.props;

    const { changed, confirmingClear, signatureOffsets } = this.state;

    let bodyNode = null;

    if (signatureOffsets) {
      bodyNode = (
        <SignatureCanvas
          penColor="black"
          onEnd={() => {
            this.setState({ changed: true });
          }}
          ref={(e) => {
            if (!e) return;
            if (onRef) onRef(e);
            if (disabled && e) e.off();
            this._canvasRef = e;
          }}
          testId={`${id}-signature-field-canvas`}
          canvasProps={{
            width: signatureOffsets.width,
            height: signatureOffsets.height,
          }}
        />
      );
    }

    const cancelClearButton = !confirmingClear ? null : (
      <Button
        size="sm"
        styles={{ marginLeft: '8px' }}
        onClick={this.handleClearCancel}
        text="Cancel"
      />
    );

    const clearButton = changed && !disabled ? (
      <Button
        size="sm"
        danger
        onClick={confirmingClear ? this.handleClear : this.handleClearConfirm}
        text={confirmingClear ? 'Confirm Clear' : 'Clear'}
      />
    ) : null;

    const legalVerbiage = primaryDisplayOnly ? null : (
      <StyledSubText theme={this.context}>
        The parties agree that this agreement may be electronically signed. The parties agree that the
        electronic signatures appearing on this agreement are the same as handwritten signatures for
        the purposes of validity, enforceability, and admissibility.
      </StyledSubText>
    );

    const canvasInput = !disabled ? (
      <StyledInputWrapper>
        <StyledInput theme={this.context} onChange={this.handleCanvasFill} placeholder="Type signature" />
      </StyledInputWrapper>
    ) : null;

    return (
      <Modal
        title="Signature"
        width={width || 430}
        isOpen={display}
        onHide={onHide}
        submitText="Save"
        onSubmit={this.handleSave}
        overlayStyles={{ zIndex: 10000000000 }}
      >
        <StyledWrapper>
          <StyledSignatureMeasurer
            theme={this.context}
            ref={(e) => {
              if (e && !signatureOffsets) {
                this.setState({ signatureOffsets: e.getBoundingClientRect() });
              }
            }}
          >
            {bodyNode}
            <StyledSignatureLine theme={this.context} />
          </StyledSignatureMeasurer>
          <StyledActionsWrapper>
            {canvasInput}
            <StyledButtonsWrapper>
              {clearButton}
              {cancelClearButton}
            </StyledButtonsWrapper>
          </StyledActionsWrapper>
          {legalVerbiage}
        </StyledWrapper>
      </Modal>
    );

  }

  handleCanvasFill = (e) => {
    const text = e.target.value;
    const { signatureOffsets } = this.state;

    // Backspacing doesn't work for native canvas so we pre-clear
    if (text.length < 1) this.handleClear();

    // Use original underlying canvas ref to use browser native
    // .fillText as react-signature-canvas doesn't support it
    const canvas = this._canvasRef.getCanvas();
    const ctx = canvas.getContext('2d');

    // Brush Script MT is the only natively cross browser
    // supported cursive font
    ctx.font = '80px Brush Script MT';

    ctx.fillText(text, 15, signatureOffsets.height / 2.2);

    this.setState({ signer: text, changed: true });
  }

  handleClearCancel = () => {
    this.setState({ confirmingClear: false });
  }

  handleClearConfirm = () => {
    this.setState({ confirmingClear: true });
  }

  handleClear = () => {

    const { fieldId, onChange, identifier } = this.props;

    if (this._canvasRef) this._canvasRef.clear();

    if (onChange) onChange(fieldId, identifier, '');

    this.setState({ changed: false, confirmingClear: false });

  }

  handleSave = () => {

    const { onSubmit } = this.props;
    const { signer, changed } = this.state;

    const canvas = this._canvasRef ? this._canvasRef.getCanvas() : null;

    /**
     * We are checking if changed is true to prevent empty signature
     * from being saved as the field value.
     */
    const dataUrl = canvas && changed ? canvas.toDataURL() : '';

    if (onSubmit) onSubmit(dataUrl, signer);

    this.setState({ changed: false });

  }

}

export default SignatureModal;
